import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import HomePage from "./pages/HomePage";
import K4ResourcePage from "./pages/K4ResourcePage";
import CompliancePage from "./pages/CompliancePage";
import IPCPage from "./pages/IPCPage";
import ModulePage from "./pages/ModulePage";
import SearchPage from "./pages/SearchPage";
import ChatPage from "./pages/ChatPage";
import QuickRefPage from "./pages/QuickRefPage";
import MBSMonitorPage from "./pages/MBSMonitorPage";
import SOPsPage from "./pages/SOPsPage";
import QualityManualPage from "./pages/QualityManualPage";
import UrgentFindingsPage from "./pages/UrgentFindingsPage";
import ClinicalEpisodesPage from "./pages/ClinicalEpisodesPage";
import DifficultPatientsPage from "./pages/DifficultPatientsPage";
import ContrastSOPPage from "./pages/ContrastSOPPage";
import AnaphylaxisSOPPage from "./pages/AnaphylaxisSOPPage";
import RadiationAuditPage from "./pages/RadiationAuditPage";
import LoginActivityPage from "./pages/LoginActivityPage";
import { DisputesListPage, DisputeFormPage, DisputeDetailPage } from "./pages/DisputesPages";
import GovernanceDashboardPage from "./pages/GovernanceDashboardPage";
import WorkCultureSOPPage from "./pages/WorkCultureSOPPage";
import AcceptCultureCharterPage from "./pages/AcceptCultureCharterPage";
import { ForgotPasswordPage, ResetPasswordPage } from "./pages/PasswordResetPages";
import ClaimAdminPage from "./pages/ClaimAdminPage";
import ManagementPage from "./pages/ManagementPage";
import PeoplePage from "./pages/PeoplePage";
import ReferrerBasePage from "./pages/ReferrerBasePage";
import DIASCompliancePage from "./pages/DIASCompliancePage";
import AnticoagScreeningPage from "./pages/AnticoagScreeningPage";
import RosterPage, { RosterRespondPage, RosterPublicSchedulePage, SwapRespondPage } from "./pages/RosterPages";
import SOPWalkthroughPage from "./pages/SOPWalkthroughPage";
import PreparationPage from "./pages/PreparationPage";
import { ScreeningIndexPage, ScreeningFormPage } from "./pages/ScreeningPages";
import { IncidentsIndexPage, IncidentReportPage, IncidentDetailPage, NearMissPosterPage } from "./pages/IncidentPages";
import { ConsentIndexPage, ConsentFormPage, ConsentSubmissionPage } from "./pages/ConsentPages";
import BrandFooter from "./components/BrandFooter";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ProfilePage from "./pages/ProfilePage";
import SignContractPage from "./pages/SignContractPage";
import ChatBubble from "./components/ChatBubble";

const CHARTER_VERSION = "1.0";

function hasAcceptedCharter(user) {
  return !!user?.culture_charter_accepted_at && user?.culture_charter_version === CHARTER_VERSION;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-[#3fa758] border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasAcceptedCharter(user)) return <Navigate to="/accept-charter" replace />;
  return (
    <>
      {children}
      <BrandFooter />
    </>
  );
}

function PMRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasAcceptedCharter(user)) return <Navigate to="/accept-charter" replace />;
  if (user.role !== 'admin' && user.role !== 'superuser') return <Navigate to="/" replace />;
  return (
    <>
      {children}
      <BrandFooter />
    </>
  );
}

function CharterAcceptRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (hasAcceptedCharter(user)) return <Navigate to="/" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasAcceptedCharter(user)) return <Navigate to="/accept-charter" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<LoginPage />} />
        <Route path="/accept-charter" element={<CharterAcceptRoute><AcceptCultureCharterPage /></CharterAcceptRoute>} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/claim-admin" element={<ProtectedRoute><ClaimAdminPage /></ProtectedRoute>} />
        <Route path="/management" element={<AdminRoute><ManagementPage /></AdminRoute>} />
        <Route path="/people" element={<PMRoute><PeoplePage /></PMRoute>} />
        <Route path="/referrers" element={<ProtectedRoute><ReferrerBasePage /></ProtectedRoute>} />
        <Route path="/dias-compliance" element={<ProtectedRoute><DIASCompliancePage /></ProtectedRoute>} />
        <Route path="/anticoag-screening" element={<ProtectedRoute><AnticoagScreeningPage /></ProtectedRoute>} />
        <Route path="/roster" element={<ProtectedRoute><RosterPage /></ProtectedRoute>} />
        <Route path="/roster/respond/:token" element={<RosterRespondPage />} />
        <Route path="/roster/swap-respond/:token" element={<SwapRespondPage />} />
        <Route path="/roster/public/:token" element={<RosterPublicSchedulePage />} />
        <Route path="/sops/walkthrough/:assignmentId" element={<ProtectedRoute><SOPWalkthroughPage /></ProtectedRoute>} />
        <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
        <Route path="/k4" element={<ProtectedRoute><K4ResourcePage /></ProtectedRoute>} />
        <Route path="/compliance" element={<ProtectedRoute><CompliancePage /></ProtectedRoute>} />
        <Route path="/ipc" element={<ProtectedRoute><IPCPage /></ProtectedRoute>} />
        <Route path="/preparation" element={<ProtectedRoute><PreparationPage /></ProtectedRoute>} />
        <Route path="/screening" element={<ProtectedRoute><ScreeningIndexPage /></ProtectedRoute>} />
        <Route path="/screening/new/:formId" element={<ProtectedRoute><ScreeningFormPage /></ProtectedRoute>} />
        <Route path="/incidents" element={<ProtectedRoute><IncidentsIndexPage /></ProtectedRoute>} />
        <Route path="/incidents/new" element={<ProtectedRoute><IncidentReportPage /></ProtectedRoute>} />
        <Route path="/incidents/culture-poster" element={<ProtectedRoute><NearMissPosterPage /></ProtectedRoute>} />
        <Route path="/incidents/:id" element={<ProtectedRoute><IncidentDetailPage /></ProtectedRoute>} />
        <Route path="/consent" element={<ProtectedRoute><ConsentIndexPage /></ProtectedRoute>} />
        <Route path="/consent/submission/:id" element={<ProtectedRoute><ConsentSubmissionPage /></ProtectedRoute>} />
        <Route path="/consent/:templateId" element={<ProtectedRoute><ConsentFormPage /></ProtectedRoute>} />
        <Route path="/dashboard" element={<PMRoute><DashboardPage /></PMRoute>} />
        <Route path="/dashboard/logins" element={<PMRoute><LoginActivityPage /></PMRoute>} />
        <Route path="/users" element={<Navigate to="/management" replace />} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/module/:slug" element={<ProtectedRoute><ModulePage /></ProtectedRoute>} />
        <Route path="/quick-ref/:slug" element={<ProtectedRoute><QuickRefPage /></ProtectedRoute>} />
        <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
        <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
        <Route path="/mbs" element={<ProtectedRoute><MBSMonitorPage /></ProtectedRoute>} />
        <Route path="/sops" element={<ProtectedRoute><SOPsPage /></ProtectedRoute>} />
        <Route path="/sops/quality-manual" element={<ProtectedRoute><QualityManualPage /></ProtectedRoute>} />
        <Route path="/sops/urgent-findings" element={<ProtectedRoute><UrgentFindingsPage /></ProtectedRoute>} />
        <Route path="/sops/clinical-episodes" element={<ProtectedRoute><ClinicalEpisodesPage /></ProtectedRoute>} />
        <Route path="/sops/difficult-patients" element={<ProtectedRoute><DifficultPatientsPage /></ProtectedRoute>} />
        <Route path="/sops/contrast" element={<ProtectedRoute><ContrastSOPPage /></ProtectedRoute>} />
        <Route path="/sops/anaphylaxis" element={<ProtectedRoute><AnaphylaxisSOPPage /></ProtectedRoute>} />
        <Route path="/sops/governance" element={<ProtectedRoute><GovernanceDashboardPage /></ProtectedRoute>} />
        <Route path="/sops/work-culture" element={<ProtectedRoute><WorkCultureSOPPage /></ProtectedRoute>} />
        <Route path="/sops/radiation-audit" element={<ProtectedRoute><RadiationAuditPage /></ProtectedRoute>} />
        <Route path="/contracts/sign/:token" element={<SignContractPage />} />
        <Route path="/disputes" element={<ProtectedRoute><DisputesListPage /></ProtectedRoute>} />
        <Route path="/disputes/new" element={<ProtectedRoute><DisputeFormPage /></ProtectedRoute>} />
        <Route path="/disputes/:id" element={<ProtectedRoute><DisputeDetailPage /></ProtectedRoute>} />
      </Routes>
      {user && !window.location.pathname.startsWith('/contracts/sign/') && <ChatBubble />}
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
